import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './SearchableSelect.module.css';

/**
 * A <select> replacement with a filter box, for lists too long to scan — the
 * country dropdown pulls every country the org has configured.
 *
 * Options are matched by `name`, and `name` is also the value: the Book Shipment
 * form stores country/city names (not ids) because that is what the enquiry
 * payload sends.
 */

export type Option = { id: string; name: string };

type Props = {
  value: string;
  onChange: (name: string) => void;
  options: Option[];
  /** Shown on the closed control when nothing is selected. */
  placeholder: string;
  /** Shown in place of the list when `options` is empty. */
  emptyText?: string;
  invalid?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
};

const SearchableSelect: React.FC<Props> = ({
  value,
  onChange,
  options,
  placeholder,
  emptyText = 'No matches',
  invalid,
  disabled,
  'aria-label': ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLLIElement>(null);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options;
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Opening starts from a clean search, focused, highlighting the current pick.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    const i = options.findIndex((o) => o.name === value);
    setActive(i >= 0 ? i : 0);
    searchRef.current?.focus();
  }, [open, options, value]);

  // A new query renumbers the list, so the old index would point at a different
  // option (or past the end).
  useEffect(() => {
    if (query) setActive(0);
  }, [query]);

  useEffect(() => {
    if (open) activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [open, active, results]);

  const pick = (o: Option) => {
    onChange(o.name);
    setOpen(false);
  };

  /** Arrow/Enter/Escape, shared by the button (closed) and the search box (open). */
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[active]) pick(results[active]);
    }
  };

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={`${styles.control} ${invalid ? styles.invalid : ''}`}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className={`${styles.value} ${value ? '' : styles.placeholder}`}>
          {value || placeholder}
        </span>
        <span className={styles.chevron} aria-hidden="true" />
      </button>

      {open && (
        <div className={styles.popover}>
          <input
            ref={searchRef}
            type="text"
            className={styles.search}
            placeholder="Search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            aria-label="Filter options"
          />

          {results.length === 0 ? (
            <p className={styles.empty}>{emptyText}</p>
          ) : (
            <ul className={styles.list} role="listbox">
              {results.map((o, i) => (
                <li
                  key={o.id}
                  ref={i === active ? activeRef : undefined}
                  role="option"
                  aria-selected={o.name === value}
                  className={[
                    styles.option,
                    i === active ? styles.active : '',
                    o.name === value ? styles.selected : '',
                  ].join(' ')}
                  onMouseEnter={() => setActive(i)}
                  /* Keep focus in the search box so the blur doesn't close the
                     list before the click registers. */
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(o)}
                >
                  {o.name}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
